// @greenlight-spec: 48-user-passthrough.md#one-app-registration-several-integrations
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPerUserOauthIntegration,
  microsoftFabricServicePrincipalSteps,
  microsoftFabricUserAccessSteps,
  microsoftFabricUserSetupSteps,
  type CatalogEntryDto,
  type MicrosoftFabricServicePrincipalPiece,
  type MicrosoftFabricUserPiece,
} from '@greenlight/shared';
import { cn } from '@/lib/utils';

import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Stepper, type StepConfig } from '@/components/install/Stepper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

import { AcquisitionGuidePanel, CopyStepsButton, GuideSteps } from './AcquisitionGuidePanel';
import { extractApiError } from './catalogRegistration';
import { DataAccessTermsPanel } from './DataAccessTermsPanel';
import {
  FABRIC_STEPS,
  buildFabricRegistrations,
  fabricPiecesFor,
  fabricReachabilityTargets,
  fabricRegistrationErrors,
  fabricStepErrors,
  firstFabricStepWithErrors,
  hasFabricRegistrationErrors,
  mergeReachabilityReports,
  type FabricIdentity,
  type FabricRegistrationErrors,
  type FabricSqlEndpointDraft,
  type FabricStep,
} from './fabricRegistration';
import { RegisterConnectivityStep, type ReachabilityReport } from './RegisterConnectivityStep';
import { SignInTestPanel, type SignInTestPanelTarget } from './SignInTest';

const PIECES: readonly { piece: MicrosoftFabricUserPiece; label: string; description: string }[] = [
  {
    piece: 'rest',
    label: 'REST and GraphQL',
    description: 'Workspaces and items, and API for GraphQL queries.',
  },
  {
    piece: 'semantic_models',
    label: 'Semantic models (DAX)',
    description: "DAX queries, filtered by each model's row-level security.",
  },
  {
    piece: 'sql',
    label: 'SQL endpoints',
    description:
      'T-SQL on a Warehouse, a Lakehouse SQL analytics endpoint, or a Fabric SQL database.',
  },
];

const IDENTITIES: readonly { identity: FabricIdentity; label: string; description: string }[] = [
  {
    identity: 'person',
    label: 'User passthrough',
    description: 'Each call runs as the signed-in person, with their own Fabric access.',
  },
  {
    identity: 'app',
    label: 'Service identity',
    description: 'Each call runs as one Entra service principal. For scheduled jobs and pipelines.',
  },
];

const STEP_LABELS: Record<FabricStep, string> = {
  setup: 'Set up',
  connect: 'Connect',
  credential: 'Credential',
  review: 'Review',
};

const STEP_DESCRIPTIONS: Record<FabricStep, string> = {
  setup: 'Choose how apps call Fabric and what they reach, then set up the Entra app below.',
  connect:
    'Tell Greenlight your tenant and where Fabric lives. Greenlight checks it can reach it before you continue.',
  credential:
    'Paste the Entra app’s client id and secret. The secret is stored and never shown again.',
  review: 'Check the integrations this creates, then register.',
};

const EMPTY_ENDPOINT: FabricSqlEndpointDraft = { host: '', database: '' };

function refusedNameOf(err: unknown): string | null {
  const body = err && typeof err === 'object' && 'body' in err ? err.body : null;
  const details = body && typeof body === 'object' && 'details' in body ? body.details : null;
  const name = details && typeof details === 'object' && 'name' in details ? details.name : null;
  return typeof name === 'string' ? name : null;
}

function FieldError({ message }: { message: string | undefined }): React.JSX.Element | null {
  return message ? <p className="text-[11.5px] text-destructive">{message}</p> : null;
}

export interface FabricRegistrationFormProps {
  entries: readonly CatalogEntryDto[];
  initialPieces: readonly MicrosoftFabricUserPiece[];
  requireDataAccessTerms: boolean;
  onDataAccessTermsAccepted?: (() => void) | undefined;
  onBack: () => void;
  onServicePrincipal: (catalogKey: string) => void;
  onRegistered: () => void;
}

/**
 * The Microsoft Fabric tile's stepper: one Entra registration, the pieces IT ticks,
 * and one Register that creates an integration per piece (and per SQL endpoint).
 * User passthrough ends on the access each person needs in Fabric.
 */
export function FabricRegistrationForm({
  entries,
  initialPieces,
  requireDataAccessTerms,
  onDataAccessTermsAccepted,
  onBack,
  onServicePrincipal,
  onRegistered,
}: FabricRegistrationFormProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const create = api.integrations.create.useMutation();
  const reachabilityCheck = api.integrations.checkReachability.useMutation();
  const existingQueryKey = ['integrations', 'fabric-names'];
  const existing = api.integrations.list.useQuery(
    existingQueryKey,
    { query: { limit: 100 } },
    { queryKey: existingQueryKey },
  );
  const egressQueryKey = ['health-egress'];
  const egress = api.health.egressReachability.useQuery(egressQueryKey, undefined, {
    queryKey: egressQueryKey,
  });
  // Unknown (still loading, or refused) keeps the outbound-firewall step.
  const connectCallbackUrl = `${window.location.origin}/connect/callback`;
  const install = {
    connectCallbackUrl,
    ...(egress.data?.status === 200 ? { customerFirewall: egress.data.body.applies } : {}),
  };
  // The list leaves out deleted integrations, whose names stay reserved; a refused
  // name joins the taken set so the next submit numbers it.
  const [refusedNames, setRefusedNames] = React.useState<string[]>([]);
  const takenNames = [
    ...(existing.data?.status === 200 ? existing.data.body.items.map((item) => item.slug) : []),
    ...refusedNames,
  ];

  const [step, setStep] = React.useState<FabricStep>('setup');
  const [furthestStep, setFurthestStep] = React.useState<FabricStep>('setup');
  const [done, setDone] = React.useState(false);
  const [identity, setIdentity] = React.useState<FabricIdentity>('person');
  const [pieces, setPieces] = React.useState<MicrosoftFabricUserPiece[]>([...initialPieces]);
  const [sqlEndpoints, setSqlEndpoints] = React.useState<FabricSqlEndpointDraft[]>([
    { ...EMPTY_ENDPOINT },
  ]);
  const [tenantId, setTenantId] = React.useState('');
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [errors, setErrors] = React.useState<FabricRegistrationErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [reachability, setReachability] = React.useState<{
    key: string;
    report: ReachabilityReport;
  } | null>(null);
  // By registration key: the name each row was registered under, and why a row failed.
  const [registered, setRegistered] = React.useState<Record<string, string>>({});
  const [failures, setFailures] = React.useState<Record<string, string>>({});
  const signInTests = React.useRef<Record<string, SignInTestPanelTarget>>({});

  const person = identity === 'person';
  const activePieces = pieces.filter((piece) => fabricPiecesFor(identity).includes(piece));
  const draft = { identity, tenantId, clientId, clientSecret, pieces: activePieces, sqlEndpoints };
  const registrations = buildFabricRegistrations(
    draft,
    entries,
    takenNames,
    registered,
    requireDataAccessTerms,
  );
  const shownPieces = activePieces.length > 0 ? activePieces : fabricPiecesFor(identity);
  const setupSteps = person
    ? microsoftFabricUserSetupSteps(shownPieces, install)
    : microsoftFabricServicePrincipalSteps(shownPieces as MicrosoftFabricServicePrincipalPiece[]);
  const stepsTitle = person
    ? 'Before you register (user passthrough)'
    : 'Before you register (service identity)';
  const fabricEntry = entries.find((e) => e.catalog_key === 'microsoft-fabric');

  const reachabilityTargets = hasFabricRegistrationErrors(
    fabricStepErrors(fabricRegistrationErrors(draft), 'connect'),
  )
    ? []
    : fabricReachabilityTargets(registrations);
  const targetsKey = reachabilityTargets.length > 0 ? JSON.stringify(reachabilityTargets) : null;
  const currentReachability =
    reachability && targetsKey !== null && reachability.key === targetsKey
      ? reachability.report
      : null;
  const busy = submitting || reachabilityCheck.isPending;

  const toggle = (piece: MicrosoftFabricUserPiece, checked: boolean): void =>
    setPieces((prev) =>
      checked ? [...prev.filter((p) => p !== piece), piece] : prev.filter((p) => p !== piece),
    );
  const setEndpoint = (index: number, patch: Partial<FabricSqlEndpointDraft>): void =>
    setSqlEndpoints((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));

  /** Checks every address as it stands; true only when all are reachable. */
  async function runReachabilityCheck(): Promise<boolean> {
    if (targetsKey === null) {
      toast.error('Tick at least one piece on Set up before checking reachability.');
      return false;
    }
    try {
      const reports = await Promise.all(
        reachabilityTargets.map(async (body) => {
          const res = await reachabilityCheck.mutateAsync({ body });
          if (res.status !== 200) throw res;
          return res.body;
        }),
      );
      const report = mergeReachabilityReports(reports);
      setReachability({ key: targetsKey, report });
      return report.reachable;
    } catch (err) {
      toast.error(extractApiError(err)?.message ?? 'The reachability check failed to run.');
      return false;
    }
  }

  async function reachabilityReady(): Promise<boolean> {
    return currentReachability?.reachable === true || (await runReachabilityCheck());
  }

  function goTo(next: FabricStep): void {
    setStep(next);
    if (FABRIC_STEPS.indexOf(next) > FABRIC_STEPS.indexOf(furthestStep)) setFurthestStep(next);
  }

  async function handleContinue(): Promise<void> {
    const stepErrors = fabricStepErrors(fabricRegistrationErrors(draft), step);
    setErrors(stepErrors);
    if (hasFabricRegistrationErrors(stepErrors)) return;
    if (step === 'connect' && !(await reachabilityReady())) return;
    const next = FABRIC_STEPS[FABRIC_STEPS.indexOf(step) + 1];
    if (next) goTo(next);
  }

  function handleBack(): void {
    const previous = FABRIC_STEPS[FABRIC_STEPS.indexOf(step) - 1];
    setErrors({});
    if (previous) setStep(previous);
    else onBack();
  }

  async function handleRegister(): Promise<void> {
    const found = fabricRegistrationErrors(draft);
    setErrors(found);
    const firstStep = firstFabricStepWithErrors(found);
    if (firstStep) {
      setStep(firstStep);
      return;
    }
    if (!(await reachabilityReady())) {
      // The result renders only on Connect.
      setStep('connect');
      return;
    }
    setSubmitting(true);
    const nowRegistered = { ...registered };
    const failed: Record<string, string> = {};
    const refused: string[] = [];
    for (const { key, body } of registrations) {
      if (nowRegistered[key] !== undefined) continue;
      try {
        const res = await create.mutateAsync({ body });
        if (res.status >= 300) throw res;
        nowRegistered[key] = body.name;
        if (res.status === 201 && isPerUserOauthIntegration(body.authMode ?? '', body.authConfig)) {
          signInTests.current[key] = {
            integrationId: res.body.id,
            credentialSlug: body.credentials[0]?.slug ?? 'default',
            name: res.body.displayName,
            catalogKey: res.body.catalogKey,
            authorizeUrl: null,
          };
        }
      } catch (err) {
        const apiError = extractApiError(err);
        const name = refusedNameOf(err);
        if (apiError?.code === 'integration.name_in_use' && name !== null) refused.push(name);
        failed[key] = apiError?.message ?? 'Could not register it. Please try again.';
      }
    }
    setSubmitting(false);
    setRegistered(nowRegistered);
    setFailures(failed);
    setRefusedNames((prev) => [...prev, ...refused]);
    const newlyRegistered = Object.keys(nowRegistered).length - Object.keys(registered).length;
    if (newlyRegistered > 0) {
      if (requireDataAccessTerms) onDataAccessTermsAccepted?.();
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    }
    if (Object.keys(failed).length > 0) return;
    const total = registrations.length;
    toast.success(total === 1 ? 'Integration registered.' : `${total} integrations registered.`);
    if (person) setDone(true);
    else onRegistered();
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (step === 'review') await handleRegister();
    else await handleContinue();
  }

  const header = (title: string, description: string): React.JSX.Element => (
    <DialogHeader>
      <div className="flex items-center gap-3">
        <IntegrationTile
          slug="microsoft-fabric"
          catalogKey="microsoft-fabric"
          displayName={fabricEntry?.display_name ?? 'Microsoft Fabric'}
          size={32}
        />
        <div className="min-w-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </div>
      </div>
    </DialogHeader>
  );

  const namesList = (
    <ul className="space-y-1">
      {registrations.map(({ key, body }) => (
        <li key={key} className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]">
          <MonoValue className="text-[11.5px]">{body.name}</MonoValue>
          {registered[key] !== undefined ? (
            <span className="text-verdant">Registered</span>
          ) : failures[key] !== undefined ? (
            <span className="text-destructive" role="alert">
              Not registered: {failures[key]}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );

  if (done) {
    const accessSteps = microsoftFabricUserAccessSteps(activePieces);
    return (
      <>
        {header(
          'Microsoft Fabric registered',
          'Apps can now request these integrations. Give people access in Fabric so their queries work.',
        )}
        <div className="space-y-5" data-testid="fabric-registered">
          {namesList}
          <section className="space-y-2" aria-labelledby="fabric-test-title">
            <h3 id="fabric-test-title" className="text-[12px] font-medium text-foreground">
              Test sign-in
            </h3>
            <p className="text-[12px] text-muted-foreground">
              Sign in as yourself to each one before anyone else does. It runs the same connect a
              person using an app runs, so it fails where theirs would.
            </p>
            <SignInTestPanel
              targets={Object.values(signInTests.current)}
              onSettled={() => void queryClient.invalidateQueries({ queryKey: ['integrations'] })}
            />
          </section>
          <section
            className="space-y-2 rounded-gl border border-border bg-muted/30 p-3"
            aria-labelledby="fabric-access-title"
            data-testid="fabric-access-steps"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 id="fabric-access-title" className="text-[12px] font-medium text-foreground">
                Give people access
              </h3>
              <CopyStepsButton title="Give people access in Microsoft Fabric" steps={accessSteps} />
            </div>
            <GuideSteps steps={accessSteps} />
          </section>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onRegistered}>
            Done
          </Button>
        </DialogFooter>
      </>
    );
  }

  const stepConfigs: StepConfig[] = FABRIC_STEPS.map((id) => ({
    id,
    label: STEP_LABELS[id],
    complete: FABRIC_STEPS.indexOf(id) < FABRIC_STEPS.indexOf(furthestStep) && id !== step,
  }));

  const setupGuide = (defaultOpen: boolean): React.JSX.Element => (
    <AcquisitionGuidePanel
      credentialKind={person ? 'oauth_client' : 'service_principal'}
      instructions=""
      docsUrl="https://learn.microsoft.com/en-us/rest/api/fabric/articles/identity-support"
      docsLabel={person ? 'Fabric delegated access docs' : 'Fabric identity docs'}
      steps={setupSteps}
      stepsTitle={stepsTitle}
      defaultOpen={defaultOpen}
    />
  );

  return (
    <>
      {header('Register Microsoft Fabric', STEP_DESCRIPTIONS[step])}

      {/* A submit sends one call per integration; locked steps keep every call on the values shown. */}
      <fieldset disabled={busy} className="m-0 min-w-0 border-0 p-0">
        <Stepper
          steps={stepConfigs}
          currentStepId={step}
          onNavigate={(id) => {
            setErrors({});
            setStep(id as FabricStep);
          }}
          label="Registration steps"
          testId="fabric-stepper"
          compact
        >
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5" noValidate>
            {step !== 'setup' && setupGuide(false)}

            {step === 'setup' && (
              <>
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-foreground">Type</legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {IDENTITIES.map((option) => {
                      const selected = identity === option.identity;
                      return (
                        <button
                          key={option.identity}
                          type="button"
                          aria-pressed={selected}
                          data-testid={`fabric-identity-${option.identity}`}
                          onClick={() => setIdentity(option.identity)}
                          className={cn(
                            'flex flex-col rounded-gl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            selected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:bg-accent/50',
                          )}
                        >
                          <span className="text-[13px] font-medium text-foreground">
                            {option.label}
                          </span>
                          <span className="text-[11.5px] leading-snug text-muted-foreground">
                            {option.description}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-foreground">
                    What apps can reach
                  </legend>
                  {PIECES.map(({ piece, label, description }) => {
                    const id = `fabric-piece-${piece}`;
                    const offered = fabricPiecesFor(identity).includes(piece);
                    return (
                      <div
                        key={piece}
                        className="flex items-start gap-2.5 rounded-gl border border-border p-3"
                      >
                        <Checkbox
                          id={id}
                          checked={offered && pieces.includes(piece)}
                          disabled={!offered}
                          onCheckedChange={(checked) => toggle(piece, checked === true)}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 space-y-0.5">
                          <Label htmlFor={id} className="text-[13px]">
                            {label}
                          </Label>
                          <p className="text-[11.5px] leading-snug text-muted-foreground">
                            {offered
                              ? description
                              : 'User passthrough only: a service principal cannot query a model with row-level security.'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <FieldError message={errors.pieces} />
                </fieldset>

                {setupGuide(true)}

                {!person && (
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    To hand an app the credentials for its own client instead of calling through
                    Greenlight, use a single form:{' '}
                    <button
                      type="button"
                      className="font-medium text-link hover:underline"
                      onClick={() => onServicePrincipal('microsoft-fabric')}
                    >
                      REST API
                    </button>{' '}
                    or{' '}
                    <button
                      type="button"
                      className="font-medium text-link hover:underline"
                      onClick={() => onServicePrincipal('fabric-warehouse')}
                    >
                      SQL endpoint
                    </button>
                    .
                  </p>
                )}
              </>
            )}

            {step === 'connect' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="fabric-tenant-id">Directory (tenant) ID</Label>
                  <Input
                    id="fabric-tenant-id"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="font-mono"
                    autoComplete="off"
                  />
                  <FieldError message={errors.tenantId} />
                </div>

                {activePieces.includes('sql') && (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-foreground">SQL endpoints</legend>
                    {sqlEndpoints.map((endpoint, index) => {
                      const rowErrors = errors.sqlEndpoints?.[index];
                      return (
                        <div key={index} className="flex items-start gap-2">
                          <div className="min-w-0 flex-1 space-y-1">
                            <Label htmlFor={`fabric-sql-host-${index}`} className="text-[11.5px]">
                              Host
                            </Label>
                            <Input
                              id={`fabric-sql-host-${index}`}
                              value={endpoint.host}
                              onChange={(e) => setEndpoint(index, { host: e.target.value })}
                              placeholder="<id>.datawarehouse.fabric.microsoft.com"
                              className="font-mono text-[12px]"
                              autoComplete="off"
                            />
                            <FieldError message={rowErrors?.host} />
                          </div>
                          <div className="min-w-0 flex-1 space-y-1">
                            <Label
                              htmlFor={`fabric-sql-database-${index}`}
                              className="text-[11.5px]"
                            >
                              Database
                            </Label>
                            <Input
                              id={`fabric-sql-database-${index}`}
                              value={endpoint.database}
                              onChange={(e) => setEndpoint(index, { database: e.target.value })}
                              placeholder="Warehouse or database name"
                              className="font-mono text-[12px]"
                              autoComplete="off"
                            />
                            <FieldError message={rowErrors?.database} />
                          </div>
                          {sqlEndpoints.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="mt-5 h-8 w-8 p-0"
                              aria-label={`Remove SQL endpoint ${index + 1}`}
                              onClick={() =>
                                setSqlEndpoints((prev) => prev.filter((_, i) => i !== index))
                              }
                            >
                              <X className="h-3.5 w-3.5" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSqlEndpoints((prev) => [...prev, { ...EMPTY_ENDPOINT }])}
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      Add SQL endpoint
                    </Button>
                    <p className="text-[11.5px] leading-snug text-muted-foreground">
                      Port 1433. Each host and database becomes its own integration.
                    </p>
                  </fieldset>
                )}

                <RegisterConnectivityStep
                  canCheck={targetsKey !== null}
                  checking={reachabilityCheck.isPending}
                  report={currentReachability}
                  onCheck={() => void runReachabilityCheck()}
                />
              </>
            )}

            {step === 'credential' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="fabric-client-id">Application (client) ID</Label>
                  <Input
                    id="fabric-client-id"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Application (client) ID of the Fabric registration"
                    className="font-mono"
                    autoComplete="off"
                  />
                  {person && (
                    <p className="text-[11.5px] leading-snug text-muted-foreground">
                      Its redirect URI is this Greenlight’s connect callback:{' '}
                      <MonoValue className="text-[11.5px]" wrap>
                        {connectCallbackUrl}
                      </MonoValue>
                    </p>
                  )}
                  <FieldError message={errors.clientId} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fabric-client-secret">Client secret</Label>
                  <Input
                    id="fabric-client-secret"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    autoComplete="new-password"
                  />
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    Stored as a secret and never shown again.
                  </p>
                  <FieldError message={errors.clientSecret} />
                </div>
              </>
            )}

            {step === 'review' && (
              <>
                <div className="space-y-1" data-testid="fabric-names">
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    {registrations.length > 0
                      ? 'Registers these integrations; apps request each one by name.'
                      : 'Tick at least one piece on Set up.'}
                  </p>
                  {namesList}
                  {Object.keys(failures).length > 0 && (
                    <p className="text-[11.5px] leading-snug text-muted-foreground">
                      Fix what failed and press Register again; the ones already registered are
                      skipped.
                    </p>
                  )}
                </div>
                <DataAccessTermsPanel
                  providerName="Microsoft Fabric"
                  injected={false}
                  userPassthrough={person}
                  {...(requireDataAccessTerms
                    ? {
                        acceptance: { accepted: termsAccepted, onAcceptedChange: setTermsAccepted },
                      }
                    : {})}
                />
              </>
            )}

            <DialogFooter className="gap-2 sm:justify-between">
              <Button type="button" variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Back
              </Button>
              {step === 'review' ? (
                <Button type="submit" disabled={requireDataAccessTerms && !termsAccepted}>
                  {reachabilityCheck.isPending
                    ? 'Checking reachability…'
                    : submitting
                      ? 'Registering…'
                      : 'Register'}
                </Button>
              ) : (
                <Button type="submit">
                  {reachabilityCheck.isPending ? 'Checking reachability…' : 'Continue'}
                </Button>
              )}
            </DialogFooter>
            <p className="sr-only" aria-live="polite">
              Step {FABRIC_STEPS.indexOf(step) + 1} of {FABRIC_STEPS.length}: {STEP_LABELS[step]}
            </p>
          </form>
        </Stepper>
      </fieldset>
    </>
  );
}
